import { http } from "@/lib/http";
import type { PrecheckReport } from "@/lib/prechecks";

import type {
  AccessRule,
  AccessRuleCreateRequest,
  Share,
  ShareCreateRequest,
  ShareExtendRequest,
  ShareRestoreRequest,
  ShareSnapshot,
  ShareSnapshotCreateRequest,
} from "./types";

export async function listShares(): Promise<Share[]> {
  const { data } = await http.get<Share[]>("/shares");
  return data;
}

export async function getShare(shareId: string): Promise<Share> {
  const { data } = await http.get<Share>(`/shares/${shareId}`);
  return data;
}

export async function createShare(payload: ShareCreateRequest): Promise<Share> {
  const { data } = await http.post<Share>("/shares", payload);
  return data;
}

/** Dry run of a create: every prerequisite and what to fix. Creates nothing. */
export async function precheckShare(payload: ShareCreateRequest): Promise<PrecheckReport> {
  const { data } = await http.post<PrecheckReport>("/shares/precheck", payload);
  return data;
}

export async function deleteShare(shareId: string): Promise<void> {
  await http.delete(`/shares/${shareId}`);
}

export async function extendShare(
  shareId: string,
  payload: ShareExtendRequest,
): Promise<Share> {
  const { data } = await http.post<Share>(`/shares/${shareId}/extend`, payload);
  return data;
}

export async function listAccessRules(shareId: string): Promise<AccessRule[]> {
  const { data } = await http.get<AccessRule[]>(`/shares/${shareId}/access`);
  return data;
}

export async function createAccessRule(
  shareId: string,
  payload: AccessRuleCreateRequest,
): Promise<AccessRule> {
  const { data } = await http.post<AccessRule>(
    `/shares/${shareId}/access`,
    payload,
  );
  return data;
}

export async function deleteAccessRule(
  shareId: string,
  ruleId: string,
): Promise<void> {
  await http.delete(`/shares/${shareId}/access/${ruleId}`);
}

export async function listShareSnapshots(
  shareId: string,
): Promise<ShareSnapshot[]> {
  const { data } = await http.get<ShareSnapshot[]>(
    `/shares/${shareId}/snapshots`,
  );
  return data;
}

export async function createShareSnapshot(
  shareId: string,
  payload: ShareSnapshotCreateRequest,
): Promise<ShareSnapshot> {
  const { data } = await http.post<ShareSnapshot>(
    `/shares/${shareId}/snapshots`,
    payload,
  );
  return data;
}

export async function deleteShareSnapshot(
  shareId: string,
  snapshotId: string,
): Promise<void> {
  await http.delete(`/shares/${shareId}/snapshots/${snapshotId}`);
}

/** Creates a new share from one of `shareId`'s snapshots. */
export async function restoreShare(
  shareId: string,
  payload: ShareRestoreRequest,
): Promise<Share> {
  const { data } = await http.post<Share>(`/shares/${shareId}/restore`, payload);
  return data;
}
